import React, { useMemo, useState, useEffect } from "react";
import {
  calculateThreadAdvanced,
  THREAD_CLASS_OPTIONS,
  THREAD_SIZE_OPTIONS,
} from "../../utils/calculations";

const ThreadPitchCalculator = ({ onSave }) => {
  const [params, setParams] = useState({
    mode: "custom",
    unit: "inch",
    threadType: "external",
    threadClass: "2A",
    majorDiameter: 0.5,
    threadsPerInch: 20,
    pitch: 1.27,
    starts: 1,
    angle: 60,
    threadStandard: "",
    threadFamily: "",
    pitchDiameterMin: "",
    pitchDiameterMax: "",
  });

  const [results, setResults] = useState(null);
  const [threadSearch, setThreadSearch] = useState("");

  useEffect(() => {
    if (params.majorDiameter > 0) {
      const calcResults = calculateThreadAdvanced(params);
      setResults(calcResults);
    }
  }, [params]);

  const handleChange = (field, value) => {
    setParams((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "unit" || field === "threadType"
        ? { threadStandard: "", threadFamily: "" }
        : {}),
    }));
  };

  const handleNumber = (field, value) => {
    setParams((prev) => ({
      ...prev,
      [field]: value === "" ? "" : parseFloat(value),
      ...(["majorDiameter", "threadsPerInch", "pitch", "angle"].includes(field)
        ? { threadStandard: "", threadFamily: "" }
        : {}),
    }));
  };

  const filteredThreads = useMemo(() => {
    const query = threadSearch.trim().toLowerCase();
    if (!query) return THREAD_SIZE_OPTIONS;
    return THREAD_SIZE_OPTIONS.filter((thread) =>
      [thread.label, thread.value, thread.unit, thread.threadSeries, thread.standard]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [threadSearch]);

  const applyThreadPreset = (threadValue) => {
    const preset = THREAD_SIZE_OPTIONS.find((thread) => thread.value === threadValue);
    if (!preset) return;

    setParams((previous) => ({
      ...previous,
      unit: preset.unit,
      threadClass:
        preset.threadFamily === "pipe"
          ? preset.threadSeries
          : previous.threadType === "internal"
            ? preset.unit === "metric" ? "6H" : "2B"
            : preset.unit === "metric" ? "6g" : "2A",
      majorDiameter: preset.majorDiameter,
      threadsPerInch: preset.tpi || previous.threadsPerInch,
      pitch: preset.pitch || previous.pitch,
      angle: preset.angle || 60,
      threadStandard: preset.value,
      threadFamily: preset.threadFamily || "",
    }));
  };

  const resetForm = () => {
    setParams({
      mode: "custom",
      unit: "inch",
      threadType: "external",
      threadClass: "2A",
      majorDiameter: 0.5,
      threadsPerInch: 20,
      pitch: 1.27,
      starts: 1,
      angle: 60,
      threadStandard: "",
      threadFamily: "",
      pitchDiameterMin: "",
      pitchDiameterMax: "",
    });
  };

  const handleSave = () => {
    if (!results || results.errors || !onSave) return;

    onSave({
      summary: `${params.majorDiameter}" | ${
        params.unit === "inch" ? `${results.tpi} TPI` : `${results.pitch} mm`
      } | ${results.threadType || params.threadType}`,
      input: {
        unit: params.unit,
        threadType: params.threadType,
        threadClass: params.threadClass,
        majorDiameter: params.majorDiameter,
        pitch:
          params.unit === "metric" ? results.pitch : `${results.tpi} TPI`,
        starts: params.starts,
        angle: `${params.angle}°`,
      },
      results: {
        pitchDiameter: results.pitchDiameter,
        minorDiameter: results.minorDiameter,
        threadDepth: results.threadDepth,
        tapDrill: results.tapDrill,
        lead: results.lead,
        bestWireSize: results.bestWireSize,
        measurementOverWires: results.measurementOverWires,
        measurementOverWiresRange: `${results.measurementOverWiresMin} - ${results.measurementOverWiresMax}`,
        pitchDiameterRange: `${results.pitchDiameterMin} - ${results.pitchDiameterMax}`,
      },
      warnings: results.warnings || [],
      confidence: results.confidence || "N/A",
    });
  };

  const copyToClipboard = () => {
    if (!results || results.errors) return;

    const text = [
      "Thread Calculator",
      `Major Diameter: ${results.majorDiameter}`,
      `Pitch: ${results.pitch}`,
      `TPI: ${results.tpi}`,
      `Lead: ${results.lead}`,
      `Pitch Diameter: ${results.pitchDiameter}`,
      `Thread Class: ${results.threadClass} (${results.classLabel})`,
      `Pitch Diameter Range: ${results.pitchDiameterMin} - ${results.pitchDiameterMax}`,
      `Minor Diameter: ${results.minorDiameter}`,
      `Thread Depth: ${results.threadDepth}`,
      `Best Wire Size: ${results.bestWireSize}`,
      `Measurement Over Wires: ${results.measurementOverWires}`,
      `Measurement Over Wires Range: ${results.measurementOverWiresMin} - ${results.measurementOverWiresMax}`,
      `Tap Drill: ${results.tapDrill}`,
    ].join("\n");

    navigator.clipboard.writeText(text);
  };

  const hasErrors = results?.errors && Object.keys(results.errors).length > 0;
  const isMetric = params.unit === "metric";
  const availableThreadClasses = THREAD_CLASS_OPTIONS.filter(
    (threadClass) =>
      threadClass.threadType === params.threadType &&
      (isMetric
        ? ["6g", "6H"].includes(threadClass.value)
        : !["6g", "6H"].includes(threadClass.value))
  );

  return (
    <div className="calculator-page">
      <div className="calculator-shell">
        <div className="calculator-header" style={{ marginBottom: "24px" }}>
          <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
            🧵 Thread Calculator
          </h1>
          <p style={{ color: "var(--text-muted)", margin: 0 }}>
            Calculate pitch diameter, minor diameter, thread depth, tap drill, and lead
          </p>
        </div>

        <div className="calculator-layout calculator-layout-wide-results">
          <div className="calculator-panel">
            <div className="form-section" style={{ marginBottom: "24px" }}>
              <h3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
                Thread Inputs
              </h3>

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label
                  htmlFor="thread-search"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                  }}
                >
                  Search Standard Threads
                </label>
                <input
                  id="thread-search"
                  type="search"
                  value={threadSearch}
                  onChange={(e) => setThreadSearch(e.target.value)}
                  placeholder="Search UNC, UNF, M6, M12..."
                  style={inputStyle}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label
                  htmlFor="thread-preset"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                  }}
                >
                  Standard Thread Preset
                </label>
                <select
                  id="thread-preset"
                  onChange={(e) => applyThreadPreset(e.target.value)}
                  style={selectStyle}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select a standard thread...
                  </option>
                  {filteredThreads.map((thread) => (
                    <option key={thread.value} value={thread.value}>
                      {thread.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label htmlFor="pitch-diameter-min" style={fieldLabelStyle}>
                  Pitch Diameter Minimum from Standard/Drawing
                </label>
                <input
                  id="pitch-diameter-min"
                  type="number"
                  step="any"
                  value={params.pitchDiameterMin}
                  onChange={(e) => handleNumber("pitchDiameterMin", e.target.value)}
                  placeholder="Optional authoritative limit"
                  style={inputStyle}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label htmlFor="pitch-diameter-max" style={fieldLabelStyle}>
                  Pitch Diameter Maximum from Standard/Drawing
                </label>
                <input
                  id="pitch-diameter-max"
                  type="number"
                  step="any"
                  value={params.pitchDiameterMax}
                  onChange={(e) => handleNumber("pitchDiameterMax", e.target.value)}
                  placeholder="Optional authoritative limit"
                  style={inputStyle}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label
                  htmlFor="thread-unit"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                  }}
                >
                  Unit
                </label>
                <select
                  id="thread-unit"
                  value={params.unit}
                  onChange={(e) => {
                    const nextUnit = e.target.value;
                    setParams((previous) => ({
                      ...previous,
                      unit: nextUnit,
                      threadClass:
                        previous.threadType === "internal"
                          ? nextUnit === "metric" ? "6H" : "2B"
                          : nextUnit === "metric" ? "6g" : "2A",
                    }));
                  }}
                  style={selectStyle}
                >
                  <option value="inch">Inch</option>
                  <option value="metric">Metric</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label
                  htmlFor="thread-type"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                  }}
                >
                  Thread Type
                </label>
                <select
                  id="thread-type"
                  value={params.threadType}
                  onChange={(e) => {
                    const nextThreadType = e.target.value;
                    setParams((previous) => ({
                      ...previous,
                      threadType: nextThreadType,
                      threadClass:
                        nextThreadType === "internal"
                          ? previous.unit === "metric" ? "6H" : "2B"
                          : previous.unit === "metric" ? "6g" : "2A",
                    }));
                  }}
                  style={selectStyle}
                >
                  <option value="external">External</option>
                  <option value="internal">Internal</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label
                  htmlFor="thread-class"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                  }}
                >
                  Thread Class
                </label>
                <select
                  id="thread-class"
                  value={params.threadClass}
                  onChange={(e) => handleChange("threadClass", e.target.value)}
                  style={selectStyle}
                >
                  {availableThreadClasses.map((threadClass) => (
                    <option key={threadClass.value} value={threadClass.value}>
                      {threadClass.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label
                  htmlFor="major-diameter"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                  }}
                >
                  Major Diameter
                </label>
                <input
                  id="major-diameter"
                  type="number"
                  step="0.001"
                  value={params.majorDiameter}
                  onChange={(e) => handleNumber("majorDiameter", e.target.value)}
                  style={inputStyle}
                />
              </div>

              {!isMetric ? (
                <div className="form-group" style={{ marginBottom: "18px" }}>
                  <label
                    htmlFor="thread-tpi"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: "var(--text-primary)",
                      fontWeight: 600,
                    }}
                  >
                    Threads Per Inch (TPI)
                  </label>
                  <input
                    id="thread-tpi"
                    type="number"
                    step="1"
                    value={params.threadsPerInch}
                    onChange={(e) =>
                      handleNumber("threadsPerInch", e.target.value)
                    }
                    style={inputStyle}
                  />
                </div>
              ) : (
                <div className="form-group" style={{ marginBottom: "18px" }}>
                  <label
                    htmlFor="thread-pitch"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: "var(--text-primary)",
                      fontWeight: 600,
                    }}
                  >
                    Pitch
                  </label>
                  <input
                    id="thread-pitch"
                    type="number"
                    step="0.001"
                    value={params.pitch}
                    onChange={(e) => handleNumber("pitch", e.target.value)}
                    style={inputStyle}
                  />
                </div>
              )}

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label
                  htmlFor="thread-starts"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                  }}
                >
                  Starts
                </label>
                <input
                  id="thread-starts"
                  type="number"
                  step="1"
                  value={params.starts}
                  onChange={(e) => handleNumber("starts", e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label
                  htmlFor="thread-angle"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                  }}
                >
                  Thread Angle
                </label>
                <input
                  id="thread-angle"
                  type="number"
                  step="1"
                  value={params.angle}
                  onChange={(e) => handleNumber("angle", e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            <div
              className="form-actions"
              style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
            >
              <button onClick={resetForm} className="btn-secondary" type="button">
                Reset
              </button>
            </div>
          </div>

          <div className="calculator-panel">
            {results && !hasErrors ? (
              <>
                <div
                  className="results-header"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginBottom: "20px",
                  }}
                >
                  <h3 style={{ color: "var(--text-primary)", margin: 0 }}>
                    Results
                  </h3>
                  <div
                    className="confidence-indicator"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    Confidence:{" "}
                    <span
                      className={`confidence-${(results.confidence || "n/a").toLowerCase()}`}
                      style={{ color: "var(--accent)", fontWeight: 700 }}
                    >
                      {results.confidence || "N/A"}
                    </span>
                  </div>
                </div>

                <div
                  className="results-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "12px",
                    marginBottom: "20px",
                  }}
                >
                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>Pitch Diameter</label>
                    <span style={resultValueStyle}>{results.pitchDiameter}</span>
                  </div>

                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>Thread Class</label>
                    <span style={resultValueStyle}>{results.threadClass}</span>
                  </div>

                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>Pitch Diameter Limits</label>
                    <span style={resultValueStyle}>
                      {results.pitchDiameterMin} - {results.pitchDiameterMax}
                    </span>
                  </div>

                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>Best Measuring Wire</label>
                    <span style={resultValueStyle}>{results.bestWireSize}</span>
                  </div>

                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>Measurement Over Wires</label>
                    <span style={resultValueStyle}>{results.measurementOverWires}</span>
                  </div>

                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>MOW Class Range</label>
                    <span style={resultValueStyle}>
                      {results.measurementOverWiresMin} - {results.measurementOverWiresMax}
                    </span>
                  </div>

                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>Minor Diameter</label>
                    <span style={resultValueStyle}>{results.minorDiameter}</span>
                  </div>

                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>Thread Depth</label>
                    <span style={resultValueStyle}>{results.threadDepth}</span>
                  </div>

                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>Tap Drill</label>
                    <span style={resultValueStyle}>{results.tapDrill}</span>
                  </div>

                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>Lead</label>
                    <span style={resultValueStyle}>{results.lead}</span>
                  </div>

                  <div style={resultItemStyle}>
                    <label style={resultLabelStyle}>{isMetric ? "Pitch" : "TPI"}</label>
                    <span style={resultValueStyle}>{isMetric ? results.pitch : results.tpi}</span>
                  </div>
                </div>

                {results.notes?.length > 0 && (
                  <div
                    style={{
                      background: "var(--bg-panel)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                    }}
                  >
                    <h4 style={{ color: "var(--text-primary)", marginTop: 0 }}>
                      Inspection Notes
                    </h4>
                    <ul
                      style={{
                        paddingLeft: "20px",
                        color: "var(--text-secondary)",
                        marginBottom: 0,
                      }}
                    >
                      {results.notes.map((note, index) => (
                        <li key={index}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {results.warnings?.length > 0 && (
                  <div
                    className="warnings"
                    style={{
                      background: "rgba(245, 158, 11, 0.12)",
                      border: "1px solid rgba(245, 158, 11, 0.35)",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                    }}
                  >
                    <h4
                      style={{
                        color: "var(--text-primary)",
                        marginBottom: "10px",
                        marginTop: 0,
                      }}
                    >
                      ⚠️ Warnings
                    </h4>
                    <ul
                      style={{
                        paddingLeft: "20px",
                        color: "var(--text-secondary)",
                        marginBottom: 0,
                      }}
                    >
                      {results.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div
                  className="results-actions"
                  style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
                >
                  <button
                    onClick={handleSave}
                    className="btn-primary"
                    type="button"
                  >
                    💾 Save Calculation
                  </button>
                  <button
                    onClick={copyToClipboard}
                    className="btn-secondary"
                    type="button"
                  >
                    📄 Copy
                  </button>
                </div>
              </>
            ) : (
              <div
                style={{
                  color: "var(--text-muted)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "180px",
                  textAlign: "center",
                }}
              >
                Enter valid inputs to calculate thread dimensions.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  width: "100%",
  minHeight: "46px",
  padding: "10px 12px",
  borderRadius: "14px",
  border: "1px solid var(--border-soft)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.02)), var(--bg-panel)",
  color: "var(--text-primary)",
  boxSizing: "border-box",
  outline: "none",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  boxShadow: "none",
  fontSize: "15px",
};

const fieldLabelStyle = {
  display: "block",
  marginBottom: "8px",
  color: "var(--text-primary)",
  fontWeight: 600,
};

const selectStyle = {
  width: "100%",
  minHeight: "46px",
  padding: "10px 38px 10px 12px",
  borderRadius: "14px",
  border: "1px solid var(--border-soft)",
  backgroundColor: "var(--bg-panel)",
  backgroundImage:
    "linear-gradient(45deg, transparent 50%, var(--text-muted) 50%), linear-gradient(135deg, var(--text-muted) 50%, transparent 50%)",
  backgroundPosition: "calc(100% - 18px) 50%, calc(100% - 12px) 50%",
  backgroundSize: "6px 6px, 6px 6px",
  backgroundRepeat: "no-repeat",
  color: "var(--text-primary)",
  boxSizing: "border-box",
  outline: "none",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  boxShadow: "none",
  fontSize: "15px",
};

const resultItemStyle = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-color)",
  borderRadius: "10px",
  padding: "14px",
};

const resultLabelStyle = {
  display: "block",
  color: "var(--text-muted)",
  fontSize: "14px",
  marginBottom: "6px",
};

const resultValueStyle = {
  color: "var(--text-primary)",
  fontWeight: 700,
  fontSize: "18px",
};

export default ThreadPitchCalculator;
